import {
  Check,
  CloudDownload,
  LoaderCircle,
  Search,
  ShieldCheck,
  Wifi,
} from "lucide-react";
import { useEffect, useState } from "react";
import { t } from "../i18n";
import { proxiedImageUrl } from "../utils/imageProxy";

type PreviewLoadingCardProps = {
  handle: string;
  progress: number;
  steps: string[];
  profilePicUrl?: string;
};

const stepIcons = [Wifi, Search, CloudDownload, LoaderCircle, ShieldCheck];

export function PreviewLoadingCard({
  handle,
  progress,
  steps,
  profilePicUrl,
}: PreviewLoadingCardProps) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const progressPercent = Math.max(8, Math.min(96, Math.round(progress * 100)));
  const currentStep = Math.min(
    steps.length - 1,
    Math.floor(progress * steps.length),
  );
  const initial = (handle.trim()[0] ?? "?").toUpperCase();
  const avatarUrl = !avatarFailed ? proxiedImageUrl(profilePicUrl) : undefined;

  useEffect(() => {
    setAvatarFailed(false);
  }, [profilePicUrl]);

  return (
    <article className="st-preview-scan" aria-busy="true">
      <header className="st-preview-scan-head">
        <h2>{t("previewAnalyzingTitle")}</h2>
        <p>@{handle}</p>
        <div className="st-preview-scan-avatar" aria-hidden="true">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              loading="eager"
              decoding="async"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span>{initial}</span>
          )}
        </div>
      </header>

      <div className="st-preview-scan-progress-row">
        <span>{t("previewProgressLabel")}</span>
        <strong>{progressPercent}%</strong>
      </div>
      <div className="st-preview-progress-track st-preview-scan-progress">
        <div
          className="st-preview-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="st-preview-scan-steps">
        {steps.map((step, index) => {
          const Icon = stepIcons[index] ?? Search;
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;
          return (
            <div
              key={step}
              className={`st-preview-scan-step ${
                isDone ? "is-done" : isCurrent ? "is-current" : ""
              }`}
            >
              <span className="st-preview-scan-step-icon">
                {isDone ? (
                  <Check size={18} strokeWidth={3} />
                ) : isCurrent ? (
                  <LoaderCircle size={18} strokeWidth={2.5} />
                ) : (
                  <Icon size={18} strokeWidth={2.5} />
                )}
              </span>
              <strong>{t(step)}</strong>
              {isDone && (
                <span className="st-preview-scan-check" aria-hidden="true">
                  <Check size={18} strokeWidth={3} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </article>
  );
}
